/**
 * Super Admin API helper
 * Handles authentication for cross-origin API calls
 *
 * When on any unplug-app.com subdomain with Cloudflare Access, the
 * CF_Authorization cookie contains the JWT. We route API calls through
 * a Pages Function proxy at /api/* which forwards these headers to the actual API.
 */

// Check if we should use the proxy (any unplug-app.com subdomain accessing super-admin)
function shouldUseProxy() {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // Use proxy on any unplug-app.com subdomain when accessing super-admin routes
  // The proxy forwards the CF_Authorization cookie as CF-Access-Jwt-Assertion header
  return (
    hostname.endsWith('.unplug-app.com') ||
    hostname === 'admin.unplug-app.com'
  );
}

// Convert absolute API URL to relative URL for proxy
function getProxiedUrl(url) {
  if (!shouldUseProxy()) return url;

  // Convert https://api.unplug-app.com/api/... to /api/...
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'api.unplug-app.com') {
      return parsed.pathname + parsed.search;
    }
  } catch (e) {
    // If it's already a relative URL, return as-is
  }
  return url;
}

// Make authenticated fetch to super admin API.
// Auth comes from the Cloudflare Access JWT cookie (credentials: 'include')
// - the former X-Session-Id workspace-session fallback was removed because
// it bypassed the Access boundary.
export async function superAdminFetch(url, options = {}) {
  // Route through proxy when on admin subdomain
  let proxiedUrl = getProxiedUrl(url);

  // Add cache-busting timestamp to prevent stale responses
  const separator = proxiedUrl.includes('?') ? '&' : '?';
  proxiedUrl = `${proxiedUrl}${separator}_t=${Date.now()}`;

  const headers = {
    ...options.headers,
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
  };

  return fetch(proxiedUrl, {
    ...options,
    headers,
    credentials: 'include',
    cache: 'no-store',
  });
}

// JSON POST helper for super admin API
export async function superAdminPost(url, data) {
  return superAdminFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// JSON PUT helper for super admin API
export async function superAdminPut(url, data) {
  return superAdminFetch(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// DELETE helper for super admin API
export async function superAdminDelete(url) {
  return superAdminFetch(url, {
    method: 'DELETE',
  });
}
